import React from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

import FormLabel from 'src/components/UI/input/CustomFormLabel';
import { getClinicalDefaults } from './formService';
import {
  STATUS_LIST,
  REASON_LIST,
  appointmentStart,
  statusOptions,
  statusTakesReason,
  statusChange,
  cancelledAfterStart
} from './appointmentStatusRules';
import {
  defaultStatusLabel,
  fallbackDefaultLabel
} from 'src/util/statusSemantics';

// THE APPOINTMENT STATUS, AND WHY IT IS WHAT IT IS (TGCLOUD-1751).
//
// One component for every form that writes an appointment's status: the
// pedigree sidebar's Appointment form (and so the Bulk Edit panel) and the
// AppBar notification modal. The AppBar one used to hard-code Open and Closed
// and quietly wrote Closed onto any record that had no status; sharing this is
// what stops the two drifting apart again.
//
// What it does, all driven by what a status MEANS rather than how it is spelled
// (see appointmentStatusRules.js for the rules themselves):
//   - offers the group's own list, in its order, plus the record's current value
//     if the list no longer carries it, so the record re-saves unchanged;
//   - holds Attended and Did Not Attend back until the appointment has started,
//     saying why, but never blocks the value the record already holds;
//   - asks WHY when the status is Cancelled or Moved, never requires an answer,
//     and clears the answer when the status moves to one that takes none;
//   - notes, without blocking, a cancellation recorded after the fact.
//
// A NEW record starts on the group's default row. The parent seeds its state
// with the shipped default (Booked) so nothing flashes empty; once the rows
// arrive, if the group's default is a different row and nobody has touched the
// field, it moves there. That move is not the user's edit, so it is reported
// with `userInitiated: false` and does not mark the form dirty.
//
// `onChange(patch, { userInitiated })` receives only the fields that changed:
// `appointmentStatus`, and `statusReason` when it is set or cleared.

const WAITS_HINT = 'Available once the appointment time has passed';
const CANCELLED_LATE_NOTE =
  "This appointment's time has passed. It will be recorded as cancelled.";

const loadRows = (listKey) =>
  Promise.resolve()
    .then(() => getClinicalDefaults(listKey))
    .then((rows) => (Array.isArray(rows) ? rows : []))
    // A list that cannot be read leaves the shipped wording on offer, which
    // still lets the record be saved.
    .catch(() => []);

export default function AppointmentStatusField({
  status,
  reason,
  appointmentDate,
  appointmentTime,
  isNew = false,
  readOnly = false,
  onChange,
  now
}) {
  const [rows, setRows] = React.useState([]);
  const [reasonRows, setReasonRows] = React.useState([]);
  const touched = React.useRef(false);
  // What the record held when the form opened, so the late-cancellation note
  // speaks only about a change made now, not about an old Cancelled record.
  const initialStatus = React.useRef(status);

  React.useEffect(() => {
    let live = true;
    loadRows(STATUS_LIST).then((r) => live && setRows(r));
    loadRows(REASON_LIST).then((r) => live && setReasonRows(r));
    return () => {
      live = false;
    };
  }, []);

  // Move a new record onto the group's own default once it is known.
  React.useEffect(() => {
    if (!isNew || touched.current || !rows.length) return;
    const configured = defaultStatusLabel(rows, STATUS_LIST);
    const shipped = fallbackDefaultLabel(STATUS_LIST);
    const untouchedValue = !status || status === shipped;
    if (configured && untouchedValue && configured !== status) {
      onChange?.({ appointmentStatus: configured }, { userInitiated: false });
    }
    // Once, when the rows land; `status` is read as it stands then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, isNew]);

  const start = appointmentStart(appointmentDate, appointmentTime);
  const at = now || new Date();
  const options = statusOptions({ rows, current: status, start, now: at });
  const anyWaiting = options.some((o) => o.disabled);
  const takesReason = statusTakesReason(status, rows);
  const lateCancellation =
    status !== initialStatus.current &&
    cancelledAfterStart({ status, rows, start, now: at });

  const reasonLabels = reasonRows.map((r) => r.label).filter(Boolean);
  const reasonValue = String(reason ?? '');
  const reasonOffered =
    !reasonValue.trim() ||
    reasonLabels.some(
      (l) => l.trim().toLowerCase() === reasonValue.trim().toLowerCase()
    )
      ? reasonLabels
      : [...reasonLabels, reasonValue];

  const handleStatus = (event) => {
    touched.current = true;
    onChange?.(statusChange(event.target.value, reason, rows), {
      userInitiated: true
    });
  };

  const handleReason = (event) => {
    touched.current = true;
    onChange?.({ statusReason: event.target.value }, { userInitiated: true });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box>
        <Box sx={{ paddingBottom: 1 }}>
          <FormLabel htmlFor="appointment-status">Appointment Status</FormLabel>
        </Box>
        <FormControl fullWidth>
          <Select
            id="appointment-status"
            inputProps={{ 'aria-label': 'Appointment Status' }}
            name="appointmentStatus"
            value={status ?? ''}
            onChange={handleStatus}
            readOnly={readOnly}
            displayEmpty
          >
            {options.map((option) => (
              <MenuItem
                key={option.label}
                value={option.label}
                disabled={option.disabled}
              >
                <Box>
                  <Typography variant="body2">{option.label}</Typography>
                  {option.disabled && (
                    <Typography variant="caption" color="text.secondary">
                      {WAITS_HINT}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
          {anyWaiting && !readOnly && (
            <FormHelperText>
              Attended and Did Not Attend become available once the appointment
              time has passed.
            </FormHelperText>
          )}
        </FormControl>
      </Box>

      {lateCancellation && !readOnly && (
        <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
          {CANCELLED_LATE_NOTE}
        </Alert>
      )}

      {takesReason && (
        <Box>
          <Box sx={{ paddingBottom: 1 }}>
            <FormLabel htmlFor="appointment-status-reason">Reason</FormLabel>
          </Box>
          <FormControl fullWidth>
            <Select
              id="appointment-status-reason"
              inputProps={{ 'aria-label': 'Reason' }}
              name="statusReason"
              value={reasonValue}
              onChange={handleReason}
              readOnly={readOnly}
              displayEmpty
            >
              <MenuItem value="">
                <em>Not recorded</em>
              </MenuItem>
              {reasonOffered.map((label) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Optional. Why the appointment was cancelled or moved.
            </FormHelperText>
          </FormControl>
        </Box>
      )}
    </Box>
  );
}
